import React, { useCallback } from 'react';
import { ImageBackground } from 'expo-image';
import { defineMessages, useIntl, type MessageValue, type NoMessageValues } from 'react-intl';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { theme, TYPE_CONFIG } from '@garnish/constants';
import { BodyText, Button, LabelText, TitleText, useResponsive } from '@garnish/core';
import { formatDate } from '@garnish/forms';
import { Modal } from '@garnish/overlays';

import { ModalBgSM, ModalBgXS } from './assets';
import { useChallengeCelebrationTelemetry } from './hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const ChallengeCelebrationModal = (props: ChallengeCelebrationModalProps) => {
  const { challengeCompletionDetails, isVisible, onDismiss, onNavigateToRewards } = props;

  const { title, celebrationCopy: description } = challengeCompletionDetails;

  // ─── Intl ─────────────────────────────────────────────────────────────

  const { formatMessage } = useIntl();

  // ─── Responsive ──────────────────────────────────────────────────────

  const { match } = useResponsive();

  // ─── Telemetry ───────────────────────────────────────────────────────

  const { trackNavigateToLoyaltyTab, trackCelebrationModalShow, trackCelebrationModalDismiss } =
    useChallengeCelebrationTelemetry();

  // ─── Derived data ────────────────────────────────────────────────────

  const expirationDate = challengeCompletionDetails.expirationDate
    ? new Date(challengeCompletionDetails.expirationDate)
    : null;
  const formattedExpirationDate = formatDate(expirationDate);

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleNavigateToRewards = useCallback(() => {
    trackNavigateToLoyaltyTab();
    onDismiss();
    onNavigateToRewards();
  }, [onDismiss, onNavigateToRewards, trackNavigateToLoyaltyTab]);

  const handleModalShown = useCallback(() => {
    trackCelebrationModalShow(challengeCompletionDetails);
  }, [challengeCompletionDetails, trackCelebrationModalShow]);

  const handleModalDismissed = useCallback(() => {
    trackCelebrationModalDismiss();
  }, [trackCelebrationModalDismiss]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const safeAreaInsets = useSafeAreaInsets();
  const hasRightSafeArea = safeAreaInsets.right > 0;
  const hasLeftSafeArea = safeAreaInsets.left > 0;

  const safeAreaContainerStyle = useStyle(
    () => ({ paddingRight: safeAreaInsets.right, paddingLeft: safeAreaInsets.left }),
    [safeAreaInsets.left, safeAreaInsets.right],
  );
  const containerSafeInsetStyle = useStyle(
    () => ({
      paddingTop: safeAreaInsets.top,
      paddingBottom: safeAreaInsets.bottom,
    }),
    [safeAreaInsets],
  );
  const contentContainerStyles = [
    styles.contentContainer,
    match([
      [
        containerSafeInsetStyle,
        hasRightSafeArea ? styles.contentContainerWithBorderRight : undefined,
        hasLeftSafeArea ? styles.contentContainerWithBorderLeft : undefined,
      ],
      undefined,
    ]),
  ];
  const safeAreaContainerStyles = [
    styles.safeAreaContainer,
    match([safeAreaContainerStyle, undefined]),
  ];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Modal
      size={match(['full', 'small'])}
      fitHeight
      animationType="fade"
      accessibilityLabel={formatMessage(messages.a11yTitle)}
      visible={isVisible}
      onRequestClose={onDismiss}
      onShown={handleModalShown}
      onDismissed={handleModalDismissed}
      withSafeAreaView={false}
    >
      <ImageBackground
        source={match([ModalBgXS, ModalBgSM])}
        contentFit="cover"
        style={styles.container}
      >
        <View style={safeAreaContainerStyles}>
          <View style={contentContainerStyles}>
            <Modal.CloseBtn palette="neutral" onPress={onDismiss} />

            <ScrollView
              contentContainerStyle={match([styles.contentContainerXS, styles.contentContainerSM])}
            >
              <Modal.Row style={styles.contentInnerContainer}>
                {title ? (
                  <TitleText sizeMatch={['40', '64', '80']} style={[styles.text, styles.title]}>
                    {title}
                  </TitleText>
                ) : null}

                {formattedExpirationDate ? (
                  <LabelText sizeMatch={['12', '14']} style={[styles.text, styles.expirationDate]}>
                    {formatMessage(messages.useBy, {
                      date: formattedExpirationDate,
                    })}
                  </LabelText>
                ) : null}

                {description ? (
                  <BodyText sizeMatch={['14', '16']} style={styles.text}>
                    {description}
                  </BodyText>
                ) : null}
              </Modal.Row>
            </ScrollView>

            <Modal.Footer withoutTopBorder style={match([styles.footerXS, styles.footerSM])}>
              <Button
                palette="primary"
                style={styles.cta}
                onPress={handleNavigateToRewards}
                size="large"
              >
                {formatMessage(messages.seeMyRewards)}
              </Button>
            </Modal.Footer>
          </View>
        </View>
      </ImageBackground>
    </Modal>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly a11yTitle: NoMessageValues;
  readonly useBy: { readonly date: MessageValue };
  readonly seeMyRewards: NoMessageValues;
}>({
  a11yTitle: {
    defaultMessage: 'Challenge reward celebration',
    description: 'Order Status | Challenge Celebration Modal | Accessibility label',
  },
  useBy: {
    defaultMessage: 'Use by {date}',
    description: 'Order Status | Challenge Celebration Modal | Expiration date label',
  },
  seeMyRewards: {
    defaultMessage: 'See my rewards',
    description: 'Order Status | Challenge Celebration Modal | CTA button',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    flexShrink: 1,
    width: '100%',
  },
  safeAreaContainer: {
    flexGrow: 1,
    flexShrink: 1,
  },
  contentContainer: {
    flexGrow: 1,
    flexShrink: 1,
  },
  contentContainerWithBorderRight: {
    borderRightWidth: 1,
    borderRightColor: theme.colors.OPACITY.BLACK.DARK,
  },
  contentContainerWithBorderLeft: {
    borderLeftWidth: 1,
    borderLeftColor: theme.colors.OPACITY.BLACK.DARK,
  },
  contentContainerXS: {
    flexGrow: 1,
    paddingTop: theme.spacing['10'],
  },
  contentContainerSM: {
    flexGrow: 1,
    paddingTop: theme.spacing['20'],
  },
  contentInnerContainer: {
    flexGrow: 1,
  },
  title: {
    ...TYPE_CONFIG.TITLE['48'],
    marginTop: 'auto',
  },
  text: {
    color: theme.colors.KALE,
    marginBottom: theme.spacing['4'],
  },
  expirationDate: {
    textTransform: 'uppercase',
  },
  cta: {
    borderWidth: 0,
  },
  footerXS: {
    marginTop: theme.spacing['6'],
  },
  footerSM: {
    marginTop: theme.spacing['6'],
    marginBottom: theme.spacing['4'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ChallengeCelebrationModalProps = {
  challengeCompletionDetails: {
    celebrationCopy: string;
    challengesIds: readonly string[];
    expirationDate?: string | null;
    title: string;
  };
  isVisible: boolean;
  onDismiss: () => void;
  onNavigateToRewards: () => void;
};
